import { Component, ErrorInfo, ReactNode } from 'react';
import logger from '../../services/loggerService';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'; // Assuming Heroicons exists

interface Props {
    children: ReactNode;
    moduleName?: string;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Module-level Error Boundary
 * Isolates failures to specific components/widgets instead of crashing the whole page.
 */
class ModuleErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        logger.error(
            `Module Error: ${this.props.moduleName || 'Unknown Module'}`,
            error,
            JSON.stringify(errorInfo)
        );
    }

    private handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="flex flex-col items-center justify-center p-6 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/30 text-center min-h-[150px]">
                    <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mb-2" />
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">
                        {this.props.moduleName || 'Component'} Unavailable
                    </h3>
                    <p className="text-xs text-red-600 dark:text-red-400 mb-4 max-w-xs">
                        We encountered a problem loading this section.
                    </p>
                    <button
                        onClick={this.handleRetry}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 rounded-md transition-colors"
                    >
                        <ArrowPathIcon className="w-3 h-3" />
                        Retry
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ModuleErrorBoundary;
